import { format, isValid } from 'date-fns';
import { safeMediaSrc } from '@/lib/mediaUrl';
import type { PostDocumentResponseDTO, PostResponseDTO } from '@/api/generated/model';

/**
 * Shared post-thumbnail / date helpers for anything rendering a `PostResponseDTO` card:
 * the "All Content" grid (UserAllContent.tsx) and the Engagement section's "Top Content"
 * row (TopContentGrid.tsx). Ports CMP `getPostThumbnailUrl` / `formatPostDate`
 * (UserDetailAllContentSection.kt, StatisticsEngagementView.kt).
 */

/** IMG -> fileUrl, VID -> thumbnail; first free-preview doc, else first pay-to-view doc when exclusive. */
export function postThumbnail(post: PostResponseDTO): string | undefined {
  const pick = (doc?: PostDocumentResponseDTO): string | undefined =>
    doc?.fileType === 'IMG' ? doc.fileUrl : doc?.fileType === 'VID' ? doc.thumbnail : undefined;
  const free = post.freePreviewDocuments?.[0];
  if (free) return pick(free);
  if (post.payExclusive && post.payToViewDocuments?.length) return pick(post.payToViewDocuments[0]);
  return undefined;
}

/** `postThumbnail` guarded against bare storage keys (safeMediaSrc — authed-media pattern). */
export function postThumbnailSrc(post: PostResponseDTO): string | undefined {
  return safeMediaSrc(postThumbnail(post));
}

/** `Jan 5, 2026` — parity with CMP `formatPostDate`, via date-fns instead of a hand-rolled month table. */
export function formatCardDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isValid(d) ? format(d, 'MMM d, yyyy') : '';
}
